import { useEffect, useState } from "react";
import { CONFIG } from "../config";
import { fmt } from "../lib/format";
import { useApp } from "../state/AppState";
import { BoutonAchat } from "./BoutonAchat";
import { Rassurance } from "./Rassurance";

/**
 * Les deux offres. Un seul produit payant, quel que soit le volet d'entrée :
 * le rapport. L'ancrage n'apparaît que si le volet 2 a produit un montant.
 */
export function Offers({ money = 0 }: { money?: number }) {
  const { parrainage, reportPrice, aSonCompte, accompagnementActif } = useApp();
  const specimen = useSpecimen();
  const preuves = usePreuves();

  return (
    <div className="offers">
      <div className="offer main">
        {/*
          Ce que la pastille dit doit être vrai — 02/09/2026.

          Elle disait « Le plus choisi ». Rien ne le mesure : aucun compteur de
          ventes par offre n'existe, et le back-office ne publie pas même le
          total. Pire, la seconde carte n'est affichée que si l'accompagnement
          est réellement assuré — il ne l'est pas — de sorte que la pastille
          annonçait une préférence entre des offres dont une seule existe.

          C'est le dernier faux signal de cet écran, après le prix barré jamais
          pratiqué, les « 500 premiers rapports » que rien ne décrémentait et
          le « offert » qui promettait ce qui est déjà gratuit. Même forme :
          une affirmation qui décore, sur l'écran qui encaisse.

          Ce qui la remplace tient la même place et se vérifie : la garantie est
          celle du produit, sans condition ni justification, et son délai est
          verrouillé côté serveur.
        */}
        <span className="badge">Remboursé sans justification</span>
        <div className="kicker" style={{ margin: "14px 0 0" }}>
          Rapport complet
        </div>
        {/*
          Le titre et les puces suivent le statut — 25/08/2026.

          C'est ici que la proposition se formait de travers. Un indépendant
          lisait « la trajectoire de repositionnement la plus courte pour
          vous » : la promesse d'un changement de poste, faite à quelqu'un qui
          n'en cherche pas. L'écran qui vend est le dernier endroit où l'on peut
          se permettre de parler à côté.

          Le contenu du rapport ne change pas de volume : dix actions, cinq
          automatisations, une section de repositionnement. Ce qui change est ce
          qu'on en dit — et pour un indépendant, ce que ces trois choses lui
          apportent n'est pas la défense d'un poste, c'est une activité qui
          tient sans lui.
        */}
        <h3>
          {aSonCompte
            ? "Ce que vous pouvez arrêter de refaire chaque semaine"
            : // Dix, comme la ligne juste en dessous, comme l'aperçu de
              // l'accueil (« action 01 sur 10 »), comme le bouton de l'écran de
              // résultat (« débloquer les neuf autres ») et comme le document
              // lui-même. « Huit » était le seul endroit du produit à dire
              // autre chose, et c'était sur la carte qu'on lit avant de payer.
              "Les dix actions qui vous concernent, vous"}
        </h3>
        {money > 0 && (
          <div className="anchor">
            Ces tâches coûtent <b>{fmt(money)} F par an</b>, au niveau de rémunération que vous avez
            indiqué. Le rapport coûte {fmt(reportPrice)} F — {fmt(Math.round(money / reportPrice))} fois
            moins.
          </div>
        )}
        {/* Une seule cellule après la puce `::before` : la grille a deux colonnes. */}
        <ul className="stack">
          <li>
            <span>
              {aSonCompte
                ? "Vos dix actions détaillées, dans l'ordre d'exécution — sur les tâches qui vous prennent vos heures facturables"
                : "Vos dix actions détaillées, dans l'ordre d'exécution"}
            </span>
          </li>
          <li>
            <span>
              {aSonCompte
                ? "Cinq automatisations qui tournent sans vous, installables cette semaine"
                : "Cinq automatisations prêtes à installer cette semaine"}
            </span>
          </li>
          <li>
            <span>
              {aSonCompte
                ? "Le repositionnement de votre offre : ce que vous vendez, à qui, à quel prix"
                : "Vers quel métier ou quel rôle évoluer, à partir de ce que vous savez déjà faire"}
            </span>
          </li>
          {/*
            Le décompte vient du serveur et porte sur des diagnostics
            réellement complétés par des filleuls distincts (ticket T15).
          */}
          {/*
            Le décompte ne se montrait qu'une fois le seuil franchi — c'est-à-dire
            quand il n'avait plus rien à provoquer. Celui qui en est à un filleul
            sur deux ne voyait rien, alors que c'est exactement la personne qu'un
            chiffre fait bouger : il lui manque une seule personne.

            À zéro, on n'affiche toujours rien. « 0 personne a passé le test
            depuis votre lien » est un chiffre exact et un signal décourageant,
            et le partage est le seul canal d'acquisition du produit.
          */}
          {/*
            « Offert » promettait quelque chose qui est déjà gratuit.

            Le diagnostic est librement répétable, par tout le monde, sans
            condition : aucune ligne du produit ne pose de limite à quatre-vingt-dix
            jours. Présenter en récompense un accès que personne n'a jamais perdu
            est un signal creux au sens de la règle non négociable n° 6 — sur
            l'écran qui encaisse, ce qui est l'endroit le plus coûteux.

            Ce qui reste est vrai et se vérifie : le rapport programme un rappel
            à quatre-vingt-dix jours, et le compteur dit combien de personnes ont
            réellement passé le test depuis ce lien. Le chiffre motive sans rien
            promettre — à zéro, il ne s'affiche pas : « 0 personne » est exact et
            décourageant, et le partage est le seul canal du produit.
          */}
          <li>
            <span>
              {parrainage && parrainage.filleuls > 0
                ? `Un rappel à 90 jours pour mesurer le mouvement — ${parrainage.filleuls} personne${
                    parrainage.filleuls > 1 ? "s ont" : " a"
                  } déjà passé le test depuis votre lien`
                : "Un rappel à quatre-vingt-dix jours pour mesurer le mouvement"}
            </span>
          </li>
        </ul>
        {/*
          Ni prix barré, ni compteur de lancement.
          Le « 7 000 F » barré désignait un tarif qui n'a jamais été pratiqué, et
          « les 500 premiers rapports » était une constante que rien ne
          décrémentait : deux faux signaux, sur l'écran qui encaisse. Ils
          affaiblissaient en plus l'ancrage réel placé juste au-dessus — celui
          par la perte annuelle, calculé sur les données de la personne et
          vérifiable par elle. Une seule promesse de prix, la vraie.
        */}
        <div className="price">
          <b>{fmt(reportPrice)} F</b>
        </div>
        <p className="launch">Prix unique, sans réduction ni prix barré</p>
        {/*
          La preuve sociale, et seulement la vraie — 28/09/2026. Deux chiffres
          lus sur les compteurs réels, recette écartée, chacun derrière son
          seuil côté serveur : sous le seuil, rien ne s'affiche, pas même zéro.
        */}
        {(preuves.rapportsLivres30j !== null || preuves.recommandation !== null) && (
          <p className="preuves fine" style={{ textAlign: "center" }}>
            {preuves.rapportsLivres30j !== null &&
              `${fmt(preuves.rapportsLivres30j)} rapports livrés ces trente derniers jours.`}
            {preuves.rapportsLivres30j !== null && preuves.recommandation !== null && " "}
            {preuves.recommandation !== null &&
              `${preuves.recommandation.promoteursPct} % des ${fmt(preuves.recommandation.notes)} personnes qui l'ont noté le recommanderaient sans hésiter (9 ou 10 sur 10).`}
          </p>
        )}
        <BoutonAchat plan="report" libelle="Obtenir mon rapport" />
        <p className="pay-note">
          Payé après avoir vu votre indice. Sans abonnement. Remboursé sur simple demande, sans
          justification.
        </p>
        <Rassurance />
        {/*
          La preuve avant l'achat, et la seule qui nous soit permise.

          La valeur du rapport ne se voyait qu'une fois payé — on demandait
          3 000 F pour un document que personne ne pouvait juger. La parade
          habituelle, les témoignages, nous est interdite par la règle n° 6, et
          à raison : on les aurait inventés faute d'en avoir.

          Reste le document lui-même. Celui qui s'ouvre ici est une sortie
          réelle du produit, sur un profil fictif — pas une maquette, et pas le
          rapport de quelqu'un.
        */}
        {/*
          Un vrai bouton, et non un lien en petits caractères.

          La première version glissait le lien dans la ligne de mention : dix-huit
          pixels de haut, c'est-à-dire intappable au pouce — la sonde responsive
          l'a refusé sur les trois formats, et elle a eu raison. Le geste qu'on
          demande ici est le plus important de la page après l'achat lui-même :
          il mérite sa cible.
        */}
        {/*
          Et il ne s'affiche que si le rapport d'exemple existe vraiment.

          Le bouton était rendu sans condition. Tant que l'exploitant n'a pas
          publié de spécimen — ce qui est l'état du produit depuis que ce bouton
          existe — il envoyait le visiteur sur une erreur. La page qui sert à
          donner une preuve avant l'achat promettait donc ce qu'elle n'avait
          pas, sur l'écran qui encaisse : la règle n° 6 dans sa forme la plus
          bête, celle qu'on écrit sans le vouloir.

          Source injoignable ou spécimen absent : pas de bouton. Un bouton mort
          coûte plus cher que pas de bouton du tout.
        */}
        {specimen && (
          <>
            <a
              className="btn btn-ghost"
              href="/specimen"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", marginTop: "10px" }}
            >
              Lire un rapport complet, en exemple
            </a>
            <p className="pay-note">Un rapport entier, sur un profil fictif. Rien à donner.</p>
          </>
        )}
      </div>

      {/*
        La formule ne s'affiche que si l'accompagnement est réellement assuré.

        Elle livrait exactement le rapport à 3 000 F : `plan` n'était lu qu'une
        fois dans toute la livraison, pour distinguer l'axe dirigeant. Six
        mille francs de plus, rien de plus, et aucune trace qu'un service était
        dû. Le serveur refuse désormais ce plan tant qu'il n'est pas déclaré
        assuré ; afficher une carte dont le paiement se solderait par un refus
        serait pire que de ne pas l'afficher.
      */}
      {/*
        Le drapeau vient du serveur, pas du paquet — 02/09/2026.

        Il se lisait dans `CONFIG`, c'est-à-dire dans `VITE_ACCOMPAGNEMENT`,
        figée à la construction. L'interrupteur posé depuis `/pilotage`
        n'atteignait donc jamais cet écran : l'offre restait invendable jusqu'à
        un redéploiement, pendant que `/pay/init` refusait consciencieusement un
        plan que personne ne pouvait demander.

        `CONFIG.accompagnementActif` sert encore d'amorce dans `AppState`, le
        temps que `/reglages` réponde.
      */}
      {accompagnementActif && (
      <div className="offer">
        <div className="kicker" style={{ margin: 0 }}>
          Rapport + accompagnement
        </div>
        {/*
          Ce que cette formule promet — révisé le 25/08/2026.

          Elle annonçait « trois mois avec Propulse ». Propulse accompagne des
          carrières, ce qui est un autre métier que celui-ci : la personne
          venait d'acheter une mesure d'exposition à l'IA, et on lui vendait par
          dessus un travail sur son CV. Deux services honnêtes, mais qui ne se
          suivent pas — et un acheteur qui ne comprend pas le lien conclut qu'il
          n'y en a pas.

          Ce qui se tient, c'est la suite du document qu'il vient de lire. Le
          rapport dit **quoi faire** ; cette formule est quelqu'un qui aide à le
          **faire**, sur les trois premières actions, puis qui commente la
          seconde mesure. Elle a un terme, et ce terme existe déjà dans le
          produit : le rendez-vous à quatre-vingt-dix jours que chaque rapport
          programme. Une formule dont on sait dire quand elle est finie est une
          formule qu'on peut honorer.

          Le troisième point ne promet aucun progrès. Il promet une mesure et
          une lecture — c'est ce qu'on sait tenir, et l'écart peut être nul.
        */}
        <h3>Le rapport, puis vos trois premières actions mises en place</h3>
        <ul className="stack">
          <li>
            <span>Tout ce que contient le rapport complet</span>
          </li>
          <li>
            <span>
              Vos trois premières actions installées avec vous, par écrit, sur WhatsApp
            </span>
          </li>
          <li>
            <span>La seconde mesure à quatre-vingt-dix jours, et l'écart commenté</span>
          </li>
        </ul>
        <div className="price">
          <b>{fmt(CONFIG.priceBundle)} F</b>
        </div>
        {/*
          « Soit 3 000 F par mois, résiliable » : le paiement est unique, il n'y
          a pas d'abonnement, donc rien à résilier et aucun remboursement des
          deuxième et troisième mois. On dit ce qui est vrai — un paiement, une
          durée.
        */}
        <p className="launch" style={{ color: "var(--text-soft)" }}>
          Un seul paiement, jusqu'à la seconde mesure
        </p>
        <BoutonAchat
          plan="bundle"
          libelle="Choisir cette formule"
          libelleReprise="Reprendre ma formule"
          className="btn"
          style={{ fontSize: "15px", padding: "14px" }}
        />
        <Rassurance />
      </div>
      )}
    </div>
  );
}

/**
 * Le spécimen est-il publié ?
 *
 * Même point d'entrée et même posture que le compteur d'usage : `/activite`
 * porte déjà l'état public du produit, et une source injoignable est traitée
 * comme une source vide. On ne montre jamais un bouton dont on ne sait pas
 * s'il mène quelque part.
 */
/** Ce que `/activite` publie de la preuve sociale — `null` sous chaque seuil. */
export interface Preuves {
  rapportsLivres30j: number | null;
  recommandation: { notes: number; promoteursPct: number } | null;
}

const SANS_PREUVE: Preuves = { rapportsLivres30j: null, recommandation: null };

/** Lit les preuves publiées. Une source injoignable vaut une source vide. */
export function lirePreuves(corps: unknown): Preuves {
  const brut = (corps as { preuves?: Partial<Preuves> } | null)?.preuves;
  if (!brut) return SANS_PREUVE;
  const livres = typeof brut.rapportsLivres30j === "number" ? brut.rapportsLivres30j : null;
  const reco = brut.recommandation;
  const recommandation =
    reco && typeof reco.notes === "number" && typeof reco.promoteursPct === "number"
      ? { notes: reco.notes, promoteursPct: reco.promoteursPct }
      : null;
  return { rapportsLivres30j: livres, recommandation };
}

export function usePreuves(): Preuves {
  const [preuves, setPreuves] = useState<Preuves>(SANS_PREUVE);
  useEffect(() => {
    let vivant = true;
    fetch(CONFIG.activiteEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: unknown) => {
        if (vivant) setPreuves(lirePreuves(corps));
      })
      .catch(() => {
        // Rien : sans source, pas de preuve affichée.
      });
    return () => {
      vivant = false;
    };
  }, []);
  return preuves;
}

export function useSpecimen(axe: "salarie" | "entreprise" = "salarie"): boolean {
  const [publie, setPublie] = useState(false);

  useEffect(() => {
    let vivant = true;
    fetch(CONFIG.activiteEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: { specimen?: boolean; specimenEntreprise?: boolean }) => {
        const dispo = axe === "entreprise" ? corps.specimenEntreprise : corps.specimen;
        if (vivant) setPublie(dispo === true);
      })
      .catch(() => {
        // Rien : l'état par défaut est « pas de spécimen ».
      });
    return () => {
      vivant = false;
    };
  }, [axe]);

  return publie;
}
